import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { auth } from "../../index";
import axios from "axios";
import { UserContext } from "../../UserContext";
import { FaMobileAlt, FaTimes } from "react-icons/fa";
import classNames from "classnames";
import "./styles.css";
import PhoneInput from "react-phone-number-input";
import ar from "react-phone-number-input/locale/ar";
import en from "react-phone-number-input/locale/en";
import "react-phone-number-input/style.css";
import OtpInput from "./OtpInput";

const PhoneVerification = ({ setIsLoggedIn }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { setUserData, setToken } = useContext(UserContext);
  const firebaseId = location.state?.firebaseId;
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState("");
  const labels = i18n.language === "ar" ? ar : en;

  useEffect(() => {
    if (!firebaseId) {
      navigate("/login");
    }
  }, [firebaseId, navigate]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError("");
    if (!phoneNumber) {
      setError(t("Please enter a valid phone number."));
      return;
    }
    try {
      // Create the verifier on-demand, right when it's needed
      const appVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
        size: "invisible",
      });

      const result = await signInWithPhoneNumber(
        auth,
        phoneNumber,
        appVerifier
      );
      setConfirmationResult(result);
      setOtpSent(true);
    } catch (error) {
      console.error("Error sending OTP:", error);
      setError(
        t("Failed to send OTP. Please check the number or try again later.")
      );
    }
  };

  const handleVerifyAndLogin = async (e) => {
    e.preventDefault();
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      setError(t("Please enter a valid 6-digit OTP."));
      return;
    }
    try {
      await confirmationResult.confirm(otpString);
      const token = await auth.currentUser.getIdToken();
      const loginResponse = await axios.post(
        "/api/auth/newLogin",
        { firebaseId },
        { headers: { Authorization: token } }
      );
      const internalUserId = loginResponse.data.items[0].User.id;
      await axios.put(
        `/api/user/updatePhoneNumber/${internalUserId}/${phoneNumber}`,
        null,
        { headers: { Authorization: token } }
      );

      setToken(token);
      setUserData(loginResponse.data.items[0]);
      setIsLoggedIn(true);
      navigate("/dashboard");
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setError(t("Invalid OTP. Please try again."));
    }
  };

  const handleSkipAndLogin = async () => {
    setError("");
    try {
      const token = await auth.currentUser.getIdToken();
      setToken(token);
      const response = await axios.post(
        "/api/auth/newLogin",
        { firebaseId },
        { headers: { Authorization: token } }
      );
      setUserData(response.data.items[0]);
      setIsLoggedIn(true);
      navigate("/dashboard");
    } catch (error) {
      console.error("Error during skip/login:", error);
      setError(
        t("Could not log you in. Please try signing in from the login page.")
      );
    }
  };

  return (
    <div className="verification-overlay">
      <div
        className={classNames("verification-modal", {
          rtl: i18n.language === "ar",
        })}
      >
        <button onClick={handleSkipAndLogin} className="verification-close-btn">
          <FaTimes />
        </button>

        <div className="verification-icon-container">
          <FaMobileAlt className="verification-icon" />
        </div>

        {otpSent ? (
          <div>
            <h2 className="verification-header">{t("Verify")}</h2>
            <form onSubmit={handleVerifyAndLogin} className="verification-form">
              <p className="otp-verification-subheader">
                {t("Your code has been sent to you via SMS.")}
              </p>
              <OtpInput otp={otp} setOtp={setOtp} />
              <p className="verification-subheader-small">
                {t(
                  "You need to verify your phone before proceeding, or simply sign up without a phone number."
                )}
              </p>
              <button type="submit" className="primary-btn">
                {t("Verify")}
              </button>
            </form>
          </div>
        ) : (
          <div>
            <h2 className="verification-header">
              {t("Please add your phone number")}
            </h2>
            <form onSubmit={handleSendOtp} className="verification-form">
              <p className="verification-subheader">
                {t("We will send a 6-digit verification code")}
              </p>
              <div className="phone-input-wrapper">
                <PhoneInput
                  international
                  placeholder={t("Phone Number")}
                  defaultCountry="EG"
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  countryCallingCodeEditable={false}
                  labels={labels}
                  className="login-phone-input"
                  required
                />
              </div>
              <button type="submit" className="primary-btn">
                {t("Verify")}
              </button>
              <button
                type="button"
                onClick={handleSkipAndLogin}
                className="secondary-btn"
              >
                {t("Skip")}
              </button>
            </form>
          </div>
        )}
        {error && <div className="error-message">{error}</div>}
        <div id="recaptcha-container"></div>
      </div>
    </div>
  );
};

export default PhoneVerification;
